// src/components/account/ForgotPasswordClient.tsx
"use client";

import React from "react";
import Link from "next/link";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import SafeHtml from "@/components/ui/SafeHtml";
import Section from "../ui/Section";
import Wrapper from "../ui/Wrapper";
import { useLayoutData } from "../layout/LayoutDataProvider";
import { apiPath } from "@/lib/apiPath";

export default function ForgotPasswordClient() {
  const { templateRoutes, global_text } = useLayoutData();
  const routes = templateRoutes ?? {};
  const loginHref = routes["page-login.php"];

  const FORGOT: Record<string, string> =
    (global_text as { auth?: { forgot_password?: Record<string, string> } } | undefined)
      ?.auth?.forgot_password ?? {};
  const t = (k: string, fallback = "") => FORGOT[k] || fallback;

  const [email, setEmail] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [emailError, setEmailError] = React.useState<string | null>(null);

  const emailRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (emailError) emailRef.current?.focus?.();
  }, [emailError]);

  function looksLikeEmail(value: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setEmailError(null);

    const trimmed = email.trim();

    if (!trimmed) {
      setEmailError(t("error_email_required", "Please enter your email."));
      return;
    }
    if (!looksLikeEmail(trimmed)) {
      setEmailError(t("error_email_invalid", "Please enter a valid email address."));
      return;
    }

    setLoading(true);
    try {
      await fetch(apiPath("/api/auth/password/reset/request"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
        body: JSON.stringify({ email: trimmed }),
      });
      setDone(true);
    } catch {
      setDone(true);
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <Section>
        <Wrapper>
          <div className="mx-auto max-w-lg">
            <div className="flex flex-col gap-6">
              <h1 className="text-title-1 font-semibold">
                {t("done_heading", "Check your email")}
              </h1>
              <p className="m-0 text-body-2">
                <SafeHtml
                  content={t("done_message_html", "If an account exists for <strong>{email}</strong>, we'll send a link to recover the password.").replace("{email}", email)}
                />
              </p>
              {loginHref && (
                <Link href={loginHref} className="text-body-2 underline">
                  {t("back_to_login_link", "Back to sign in")}
                </Link>
              )}
            </div>
          </div>
        </Wrapper>
      </Section>
    );
  }

  return (
    <Section>
      <Wrapper>
        <div className="mx-auto max-w-lg">
          <div className="flex flex-col gap-6">
            <h1 className="text-title-1 font-semibold">
              {t("heading", "Recover password")}
            </h1>

            <p className="m-0 text-body-2">
              {t("intro", "Enter your email and we'll send you a recovery link.")}
            </p>

            <form onSubmit={submit} noValidate className="flex flex-col gap-6">
              <div className="w-full min-w-0">
                <Input
                  id="forgot-email"
                  ref={emailRef}
                  label={<SafeHtml content={t("email_label", "Email")} />}
                  type="email"
                  value={email}
                  onChange={(e) => { setEmailError(null); setEmail(e.target.value); }}
                  autoComplete="email"
                  aria-invalid={!!emailError || undefined}
                  aria-describedby={emailError ? "forgot-email-error" : undefined}
                  invalid={!!emailError}
                  required
                />
                {emailError && (
                  <small id="forgot-email-error" role="alert" className="mt-1 block text-body-3 text-fuji-red">
                    {emailError}
                  </small>
                )}
              </div>

              <div className="flex w-full flex-col gap-3">
                <Button type="submit" className="group w-full" disabled={loading} loading={loading} center>
                  <SafeHtml
                    content={
                      loading
                        ? t("submitting_button", "Sending…")
                        : t("submit_button", "Recover password")
                    }
                  />
                </Button>
                {loginHref && (
                  <Link href={loginHref} className="text-body-2 underline">
                    {t("back_to_login_link", "Back to sign in")}
                  </Link>
                )}
              </div>
            </form>
          </div>
        </div>
      </Wrapper>
    </Section>
  );
}